import { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Search, CreditCard, Calendar, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { salesOf, orgUsersOf, ROLES, findUserById, effectiveBranchesOf, branchUsersOf, getOrgSettings, deliveriesOf, isModuleOn } from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';
import { FilterPopover } from '../../components/FilterPopover';
import { PrintBillModal } from '../../components/PrintBillModal';
import { CustomSelect } from '../../components/CustomSelect';
import { billNumberOf } from '../../utils/billNumber';

export const SalesLedgerTab = ({ org, user, role, branchId }) => {
  const { version: dataVersion } = useSharedStore();
  const [search, setSearch] = useState('');
  // Default to MY SALES for regular users, ALL STAFF for admins
  // Default filters
  const [filters, setFilters] = useState({ users: [], branches: [], methods: [], types: [] });
  const [view, setView] = useState(null);

  const branchesEnabled = getOrgSettings(org.id).branchesEnabled;

  const users = useMemo(() => {
    if (role === ROLES.BRANCH_ADMIN && branchId) {
      return branchUsersOf(branchId);
    }
    let allUsers = orgUsersOf(org.id).filter(u => u.role !== ROLES.WAREHOUSE_ADMIN && u.role !== ROLES.WAREHOUSE_USER);
    if (filters.branches && filters.branches.length > 0) {
      allUsers = allUsers.filter((u) => filters.branches.includes(u.branchId));
    }
    return allUsers;
  }, [org.id, role, branchId, filters.branches, dataVersion]);
  const branches = useMemo(() => effectiveBranchesOf(org.id), [org.id, dataVersion]);
  const deliveries = useMemo(() => deliveriesOf(org.id), [org.id, dataVersion]);

  const list = useMemo(() => {
    const scopedUserId = role === ROLES.ORG_USER ? user?.id : null;
    let all = salesOf(org.id, scopedUserId);
    if (role === ROLES.BRANCH_ADMIN && branchId) {
      all = all.filter((b) => b.locationId === branchId);
    }
    
    if (filters.users?.length > 0) {
      all = all.filter(b => filters.users.includes(b.userId));
    }
    if (filters.branches?.length > 0) {
      all = all.filter(b => filters.branches.includes(b.locationId));
    }
    if (filters.methods?.length > 0) {
      // Bills store 'Cash' / 'Card' while the filter options are 'CASH' / 'CARD',
      // so compare upper-cased. SalesReportTab already does this; this tab did
      // not, which made the Payment Method filter match nothing at all.
      all = all.filter(b => filters.methods.includes((b.method || '').toUpperCase()));
    }
    if (filters.types?.length > 0) {
      all = all.filter(b => filters.types.includes(b.orderType));
    }
    if (filters.deliveryStatus?.length > 0) {
      all = all.filter(b => {
        const d = deliveries.find(del => del.id === b.id);
        return d && filters.deliveryStatus.includes(d.status);
      });
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      all = all.filter(b => (String(b.id || '')).toLowerCase().includes(q) || (b.customer || '').toLowerCase().includes(q));
    }
    
    return all.sort((a, b) => {
      const dateA = a.createdAt || a.date || '';
      const dateB = b.createdAt || b.date || '';
      return new Date(dateB) - new Date(dateA);
    });
  }, [search, filters, org.id, role, user, branchId, dataVersion, deliveries]);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  useEffect(() => { setPage(1); }, [search, filters]);

  const totalPages = Math.max(1, Math.ceil(list.length / perPage));
  const start = (page - 1) * perPage;
  const pageList = list.slice(start, start + perPage);

  const accent = org.theme?.primary || 'var(--theme-primary)';

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0">
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-md px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between shadow-sm">
        <div className="flex flex-1 min-w-0 items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search bill id or customer…"
              className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-md pl-9 pr-4 py-1.5 text-xs font-medium text-[#0d0d0d] outline-none transition-colors" />
          </div>

          {role !== ROLES.ORG_USER && (
            <div className="flex items-center shrink-0">
              <FilterPopover 
                accent={accent}
                initialFilters={filters}
                onApply={setFilters}
                categories={[
                  ...(users.length > 0 ? [{
                    id: 'users',
                    label: 'User',
                    options: users.map(u => {
                      const bName = branches.find(b => b.id === u.branchId)?.name;
                      const formattedRole = u.role ? ` [${u.role.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}]` : '';
                      return { id: u.id, label: (bName && branchesEnabled) ? `${u.name} (${bName})${formattedRole}` : `${u.name}${formattedRole}` };
                    })
                  }] : []),
                  ...(role !== ROLES.BRANCH_ADMIN && branchesEnabled ? [{
                    id: 'branches',
                    label: 'Branch',
                    options: branches.map(b => ({ id: b.id, label: b.name }))
                  }] : []),
                  {
                    id: 'methods',
                    label: 'Payment Method',
                    options: [
                      { id: 'CASH', label: 'Cash' },
                      { id: 'CARD', label: 'Card' }
                    ]
                  },
                  {
                    id: 'types',
                    label: 'Order Type',
                    options: [
                      { id: 'in-shop', label: 'In Shop' },
                      { id: 'booking', label: 'Booking' },
                      ...(isModuleOn(org, 'delivery') ? [{ id: 'set-to-deliver', label: 'Delivery' }] : [])
                    ]
                  },
                  ...(isModuleOn(org, 'delivery') ? [{
                    id: 'deliveryStatus',
                    label: 'Delivery Status',
                    options: [
                      { id: 'new', label: 'New' },
                      { id: 'preparing', label: 'Preparing' },
                      { id: 'dispatched', label: 'On Road' },
                      { id: 'delivered', label: 'Delivered' },
                      { id: 'cancelled', label: 'Cancelled' }
                    ]
                  }] : [])
                ]}
              />
            </div>
          )}
        </div>

      </section>

      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden relative shadow-sm flex flex-col min-h-0 w-full flex-1">
        <div className="overflow-auto overscroll-none flex-1 custom-scrollbar min-h-0 table-scrollbar">
          <table className="w-full text-left relative">
            <thead className="bg-white sticky top-0 z-10 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 shadow-[0_1px_0_0_var(--theme-tertiary)]">
              <tr>
                <th className="px-4 py-3">Bill</th>
                <th className="px-3 py-3">Customer</th>
                <th className="px-3 py-3">User</th>
                <th className="px-3 py-3">Date</th>
                <th className="px-3 py-3">Method</th>
                <th className="px-3 py-3 text-right">Subtotal</th>
                <th className="px-3 py-3 text-right">CGST</th>
                <th className="px-3 py-3 text-right">SGST</th>
                <th className="px-3 py-3 text-right">IGST</th>
                <th className="px-3 py-3 text-right">Total</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3 text-right">View</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--theme-tertiary)]">
              {pageList.map((b) => {
                const cashier = findUserById(b.userId);
                return (
                  <tr key={b.id} className="hover:bg-slate-50 group">
                    <td className="px-4 py-3 text-[12px] font-extrabold text-[#0d0d0d] whitespace-nowrap">{billNumberOf(b)}</td>
                    <td className="px-3 py-3 text-[12px] text-slate-700 whitespace-nowrap">{b.customer || '—'}</td>
                    <td className="px-3 py-3 text-[12px] text-slate-700">{cashier?.name || '—'}</td>
                    <td className="px-3 py-3 text-[11px] text-slate-500 whitespace-nowrap">{b.date ? String(b.date).split('T')[0] : '—'}</td>
                    <td className="px-3 py-3 text-[11px] text-slate-500">{b.method}</td>
                    <td className="px-3 py-3 text-[12px] text-right text-slate-700">₹{(b.subtotal || 0).toFixed(2)}</td>
                    <td className="px-3 py-3 text-[12px] text-right text-slate-700">₹{(b.totalCgst || 0).toFixed(2)}</td>
                    <td className="px-3 py-3 text-[12px] text-right text-slate-700">₹{(b.totalSgst || 0).toFixed(2)}</td>
                    <td className="px-3 py-3 text-[12px] text-right text-slate-700">₹{(b.totalIgst || 0).toFixed(2)}</td>
                    <td className="px-3 py-3 text-[12px] text-right font-extrabold text-[#0d0d0d]">₹{(b.total || 0).toFixed(2)}</td>
                    <td className={`px-3 py-3 text-[11px] font-bold uppercase tracking-[0.05em] ${b.orderType === 'set-to-deliver' ? 'text-amber-500' : (b.orderType === 'booking' ? 'text-blue-500' : 'text-slate-400')}`}>
                      {b.orderType === 'set-to-deliver'
                        ? (deliveries.find(d => d.id === b.id)?.status || 'Delivery')
                        : (b.orderType === 'dine-in' ? 'Dine-In' : (b.orderType === 'booking' ? 'Booking' : 'In-Shop'))}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button type="button" aria-label={`View bill ${billNumberOf(b)}`} onClick={() => setView(b)} className="w-8 h-8 inline-flex items-center justify-center text-slate-400 hover:text-[var(--theme-primary)] transition-colors">
                        <Eye size={15} strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr><td colSpan={12} className="px-5 py-16 text-center text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">No bills yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {list.length > 10 && (
          <section className="px-5 py-3 flex items-center justify-between text-[11px] font-bold text-slate-500 border-t border-[var(--theme-tertiary)] bg-white shrink-0">
            <div className="flex items-center gap-2">
              <span className="uppercase tracking-widest">Showing</span>
              <CustomSelect
                value={perPage}
                onChange={(val) => {
                  setPerPage(Number(val));
                  setPage(1);
                }}
                options={[
                  { value: 10, label: '10' },
                  { value: 25, label: '25' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' },
                  { value: list.length > 0 ? list.length : 200, label: 'All' }
                ]}
                className="w-16 bg-white border border-[var(--theme-tertiary)] rounded-sm px-2 py-1 text-[11px] font-bold text-slate-700 outline-none hover:border-[var(--theme-primary)] transition-colors cursor-pointer"
                menuUp={true}
              />
            </div>
            <div className="flex items-center gap-4">
              <span className="uppercase tracking-widest">
                PAGE <strong className="text-slate-800 mx-0.5">{page}</strong> OF <strong className="text-slate-800 ml-0.5">{totalPages}</strong>
              </span>
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 rounded-md bg-white border border-[var(--theme-tertiary)] flex items-center justify-center text-slate-500 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] disabled:opacity-40 disabled:hover:border-[var(--theme-tertiary)] disabled:hover:text-slate-500 transition-all shadow-sm">
                  <ChevronLeft size={14} />
                </button>
                <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 rounded-md bg-white border border-[var(--theme-tertiary)] flex items-center justify-center text-slate-500 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] disabled:opacity-40 disabled:hover:border-[var(--theme-tertiary)] disabled:hover:text-slate-500 transition-all shadow-sm">
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </section>
        )}
      </section>

      {view && (
        <PrintBillModal bill={view} org={org} onClose={() => setView(null)} />
      )}
    </div>
  );
};

SalesLedgerTab.propTypes = {
  org: PropTypes.object.isRequired,
  user: PropTypes.object,
  role: PropTypes.string,
  branchId: PropTypes.string,
};
